import React, { useEffect, useRef, useState } from 'react';
import { styles } from './CouponModal.css';
import Button from '@jbhi-fi/lanyard-ui/components/Button';
import CalloutFont from '@jbhi-fi/lanyard-ui/font/CalloutFont';
import PromoTag from '@jbhi-fi/lanyard-ui/components/PromoTag';
import { Delete, PerksMascotIllustrated, ScrollDownArrow, TickOutlined } from '@jbhi-fi/lanyard-ui/components/Icons';
import { CouponContent } from '../types';
import { BarcodeContainer } from './BarcodeContainer';
import Tag from '@jbhi-fi/lanyard-ui/components/Tag/Tag';

type CouponModalContentProps = {
  couponCode: string;
  couponExternalId: number;
  expiryDate?: string;
  isLoading: boolean;
  isApplied: boolean;
  playCouponAppliedAnimation?: boolean;
  redemptionCondition?: 'instore' | 'online';
  onApplyCoupon: (code: string) => void;
  onRemoveCoupon: (code: string) => void;
} & CouponContent;

export const CouponModalContent = ({
  displayName,
  couponCode,
  expiryDate,
  actionsLabel,
  barcodeLabel,
  barcode,
  additonalInstructions,
  image,
  termsLabel,
  termsAndConditions,
  isLoading,
  isApplied,
  redemptionCondition,
  onApplyCoupon,
  onRemoveCoupon,
}: CouponModalContentProps) => {
  const actionsRef = useRef<HTMLDivElement | null>(null);
  const [isBarcodeVisible, setIsBarcodeVisible] = useState(false);
  const buttonLabel = isApplied ? 'Applied' : 'Apply coupon to My Cart';
  const [isRemovingCoupon, setIsRemovingCoupon] = useState(false);
  const handleRemoveCoupon = async (couponCode: string) => {
    setIsRemovingCoupon(true);
    await onRemoveCoupon(couponCode);
    setIsRemovingCoupon(false);
  };

  const defaultActionsLabel = redemptionCondition === 'instore' ? 'Redeem in store' : 'Redeem online';

  useEffect(() => {
    if (actionsRef.current) {
      const observer = new IntersectionObserver(
        ([entry]) => {
          setIsBarcodeVisible(entry.isIntersecting);
        },
        {
          threshold: 0.4,
        }
      );
      observer.observe(actionsRef.current);

      return () => {
        observer.disconnect();
      };
    }
  }, []);

  return (
    <>
      <div className={styles.container}>
        <div className={styles.content}>
          <div className={styles.banner}>
            <div className={styles.iconContainer}>
              {image ? <img src={image.src} alt={image.alt} /> : <PerksMascotIllustrated />}
            </div>
            <CalloutFont as="h2" className={styles.iconHeading}>
              {displayName}
            </CalloutFont>
            {expiryDate ? <PromoTag label={`Expires: ${expiryDate}`} variant="main" /> : null}
          </div>
          {redemptionCondition ? <RedemptionConditionTag condition={redemptionCondition} /> : null}
          {termsAndConditions ? (
            <div className={styles.terms}>
              <h3 className={styles.termsLabel}>{termsLabel || 'Things to know'}</h3>
              {termsAndConditions}
            </div>
          ) : null}
        </div>
        <div className={styles.actions} ref={actionsRef}>
          {redemptionCondition !== 'instore' ? (
            <div className={styles.actionsContainer}>
              <h5 className={styles.actionsLabel}>{actionsLabel || defaultActionsLabel}</h5>
              <>
                <Button
                  classes={styles.actionsButton}
                  onClick={() => onApplyCoupon(couponCode)}
                  type="action"
                  icon={isApplied ? <TickOutlined /> : undefined}
                  label={buttonLabel}
                  loading={isLoading}
                  disabled={isApplied}
                  iconPosition="left"
                />
                {isApplied ? (
                  <Button
                    type="linkWithIcon"
                    icon={<Delete />}
                    label="Remove"
                    loading={isRemovingCoupon}
                    onClick={() => handleRemoveCoupon(couponCode)}
                    iconPosition="left"
                    classes={styles.deleteIcon}
                  />
                ) : null}
              </>
            </div>
          ) : null}

          {redemptionCondition !== 'online' ? (
            <BarcodeContainer
              barcode={barcode}
              barcodeLabel={barcodeLabel}
              couponCode={couponCode}
              additonalInstructions={additonalInstructions}
            />
          ) : null}
        </div>
      </div>
      {!isBarcodeVisible && (
        <div className={styles.scrollIndicator({ showScrollIndicator: !isBarcodeVisible })}>
          <div className={styles.scrollIndicatorBackground}></div>
          <div className={styles.scrollIndicatorTextContainer}>
            <ScrollDownArrow /> Scroll to redeem
          </div>
        </div>
      )}
    </>
  );
};

const RedemptionConditionTag = ({ condition }: { condition: 'instore' | 'online' }) => {
  const redemptionConditionTags = {
    instore: 'In-store only',
    online: 'Online only',
  };

  const label = redemptionConditionTags[condition];

  return <Tag className={styles.redmptionCondition} content={label} type="yellow" />;
};
